import React from 'react'
import moment from 'moment'
import { navigate } from '@reach/router'
import { ValidationErrorResult } from './store/State'
import { IInitiativeSubmissionProviderProps, IInitiativeSubmissionViewProps } from './Interfaces'
import Provider from './Provider'
import {
  EnterpriseContainer,
  PointrThemedFormItem,
  EnterpriseBottomBar,
  AlertDialog,
  InitiativeSubmissionFormItem
} from 'components'
import { Paper, RadioGroup, FormControlLabel, Radio } from '@material-ui/core'
import useInitiativeSubmissionStyles from './Styles'
import IInitiativeSubmissionQuestions, { QuestionType } from 'types/IInitiativeSubmissionQuestions'
import _ from 'lodash'

function getErrorMessage(error: ValidationErrorResult) {
  switch (error) {
    case 'EMPTY_ANSWER':
      return 'Ooops! This question is not allowed to be empty'
    case 'INVALID_LINK':
      return 'Ooops! This answer is not a valid URL'
    default:
      return null
  }
}

const InitiativeSubmissionPage: React.FC<IInitiativeSubmissionViewProps> = ({
  state,
  onSubmitAnswers,
  toggleSuccessDialog,
  toggleIncompleteProfileDialog,
  toggleValidationError,
  setQuestionAnswer,
  setQuestionChoice,
  setGpaValue,
  setGpaGrade,
  onSaveInitiativeDraft
}) => {
  const classes = useInitiativeSubmissionStyles({})

  const lastAutoSave = state.lastAutoSaveDate ? moment(state.lastAutoSaveDate) : null
  const systemDateTime = state.currentDateTime ? moment(state.currentDateTime) : null

  let elapsedMinutes = '0'

  if (!!lastAutoSave && !!systemDateTime && state.lastAutoSaveDate < state.currentDateTime) {
    elapsedMinutes = systemDateTime.diff(lastAutoSave, 'minutes').toString()
  }

  const onCloseSuccessDialog = () => {
    toggleSuccessDialog()
    navigate('/snapshot')
  }

  const onCloseIncompleteProfileDialog = () => {
    toggleIncompleteProfileDialog()
    navigate('/top-fives?displayErrors=true')
  }

  const onFormItemChange = (item: IInitiativeSubmissionQuestions) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    selected?: boolean
  ) => {
    if (item.questionType === QuestionType.CHECKBOX) {
      if (!item.choices) {
        item.choices = []
      }

      if (selected) {
        item.choices.push(+event.target.value)
      } else {
        item.choices = item.choices.filter(choice => choice !== +event.target.value)
      }
      setQuestionChoice(item.id, _.uniq(item.choices))
    } else if (item.questionType === QuestionType.MULTIPLE_CHOICE) {
      const choices = selected ? [+event.target.value] : []
      setQuestionChoice(item.id, choices)
    } else {
      setQuestionAnswer(item.id, event.target.value)
    }
  }

  return (
    <React.Fragment>
      <AlertDialog
        content="Please fill all the question before submit"
        isOpen={state.validationError}
        size="sm"
        title="All the fields are required"
        onClose={toggleValidationError}
      />

      <AlertDialog
        content="Thank you for your submission"
        isOpen={state.isSuccessDialogOpen}
        size="sm"
        title="Submission completed"
        closeButtonText="Return to your profile"
        onClose={onCloseSuccessDialog}
      />

      <AlertDialog
        content="To submit to an initiative your Pointr profile must be complete. Please go back to your profile and complete the required questions"
        isOpen={state.isIncompleteProfileDialogOpen}
        size="sm"
        title="Your profile ins't fully complete"
        closeButtonText="Go back to Pointr profile"
        onClose={onCloseIncompleteProfileDialog}
      />

      <EnterpriseContainer hasBottomBar={true} enterpriseLogo={state.initiativeData?.enterpriseLogo}>
        <Paper className={classes.paper}>
          <div className={classes.titleContainer}>
            <h1 id="initiative-submission-title" className={classes.titleText}>
              {state.initiativeData?.title}
            </h1>
          </div>
          <div>
            <h3 id="initiative-submission-sub-title" className={classes.subtitle}>
              {state.initiativeData?.submissionPageCopyText}
            </h3>
          </div>
          <div className={classes.questionsContainer}>
            {(state.initiativeData?.questions || []).map((item, index) => {
              const validationError = state.validationState[item.id]
              const message = getErrorMessage(validationError)
              const question = state.answers.questions[item.id] || {}
              return (
                <React.Fragment key={index}>
                  <InitiativeSubmissionFormItem
                    classes={{ label: classes.questionsLabel }}
                    item={item}
                    helperText={message}
                    value={question.answer}
                    validationError={validationError}
                    onChange={onFormItemChange(item)}
                    selected={question.choices}
                  />
                </React.Fragment>
              )
            })}
          </div>
          {state.initiativeData?.hasGpaQuestion && (
            <div className={classes.gpaContainer}>
              <div className={classes.gpaValueInputContainer}>
                <PointrThemedFormItem
                  label="GPA"
                  value={state.answers?.gpa?.toString()}
                  disabled={state.answers.gpaGrade === 'DOES_NOT_APPLY'}
                  placeholder="out of 4.0"
                  type="number"
                  onChange={event => setGpaValue(event.target.value)}
                />
              </div>
              <div className={classes.gpaGradeContainer}>
                <RadioGroup
                  classes={{
                    root: classes.radioButtonGroupRoot
                  }}
                  value={state.answers.gpaGrade}
                  onChange={event => setGpaGrade(event.target.value as 'GRADUATE' | 'UNDERGRADUATE' | 'DOES_NOT_APPLY')}
                >
                  <FormControlLabel
                    id="initiative-submission-undergraduate-label"
                    className={classes.radioButtonLabelContainer}
                    classes={{ label: classes.radioButtonLabel }}
                    value="UNDERGRADUATE"
                    control={
                      <Radio
                        classes={{
                          checked: classes.radioButtonChecked
                        }}
                      />
                    }
                    label="Undergraduate"
                  />
                  <FormControlLabel
                    id="initiative-submission-graduate-label"
                    className={classes.radioButtonLabelContainer}
                    classes={{ label: classes.radioButtonLabel }}
                    value="GRADUATE"
                    control={
                      <Radio
                        classes={{
                          checked: classes.radioButtonChecked
                        }}
                      />
                    }
                    label="Graduate"
                  />
                  <FormControlLabel
                    id="initiative-submission-my-school-doesnt-use-this-grading-system-label"
                    className={classes.radioButtonLabelContainer}
                    classes={{ label: classes.radioButtonLabel }}
                    value="DOES_NOT_APPLY"
                    control={
                      <Radio
                        classes={{
                          checked: classes.radioButtonChecked
                        }}
                      />
                    }
                    label="My school doesn’t use this grading system"
                  />
                </RadioGroup>
              </div>
            </div>
          )}
        </Paper>
        <EnterpriseBottomBar
          lastAutoSave={elapsedMinutes}
          onSaveDraftClick={onSaveInitiativeDraft}
          onSubmit={onSubmitAnswers}
          isSubmitting={state.isSubmitting}
        />
      </EnterpriseContainer>
    </React.Fragment>
  )
}

export default (props: IInitiativeSubmissionProviderProps) => (
  <Provider {...props}>
    {(viewProps: IInitiativeSubmissionViewProps) => <InitiativeSubmissionPage {...viewProps} />}
  </Provider>
)
